/**
 * Settings - Application configuration
 */
import {
  AlertCircle,
  Bell,
  CheckCircle,
  Database,
  ExternalLink,
  Eye,
  EyeOff,
  Key,
  Palette,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'api' | 'appearance' | 'notifications'>('api');
  const [showKeys, setShowKeys] = useState(false);
  const [message] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [config, setConfig] = useState({
    openaiConfigured: false,
    supabaseConfigured: false,
  });

  useEffect(() => {
    // Load current configuration status
    const loadConfig = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/health`);
        const data = await res.json();
        setConfig({
          openaiConfigured: data.openai_configured,
          supabaseConfigured: data.supabase_configured,
        });
      } catch (err) {
        console.error('Failed to load config:', err);
      }
    };
    loadConfig();
  }, []);

  const tabs = [
    { id: 'api', label: 'API & Connexions', icon: <Key className="w-4 h-4" /> },
    { id: 'appearance', label: 'Apparence', icon: <Palette className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
        <p className="text-gray-500">Configurez votre application</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
          }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-500" />
          )}
          <span className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
            {message.text}
          </span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* API Tab */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              {/* OpenAI */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">🤖</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">OpenAI API</h3>
                      <p className="text-sm text-gray-500">GPT-4.1 pour la génération de contenu</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.openaiConfigured
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                    }`}>
                    {config.openaiConfigured ? 'Connecté' : 'Non configuré'}
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Clé API
                    </label>
                    <div className="flex gap-2">
                      <input
                        type={showKeys ? 'text' : 'password'}
                        placeholder="sk-..."
                        disabled
                        value={config.openaiConfigured ? '••••••••••••••••••••' : ''}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                      <button
                        onClick={() => setShowKeys(!showKeys)}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {showKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Configurée via variable d'environnement OPENAI_API_KEY
                    </p>
                  </div>
                </div>
              </div>

              {/* Supabase */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Database className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Supabase</h3>
                      <p className="text-sm text-gray-500">Base de données PostgreSQL</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.supabaseConfigured
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                    }`}>
                    {config.supabaseConfigured ? 'Connecté' : 'Non configuré'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL du projet
                    </label>
                    <input
                      type="text"
                      placeholder="https://xxx.supabase.co"
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Clé Anon
                    </label>
                    <input
                      type="password"
                      placeholder="eyJ..."
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Configurées via variables d'environnement SUPABASE_URL et SUPABASE_KEY
                </p>
              </div>

              {/* Environment info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Configuration via fichier .env</h4>
                <pre className="bg-gray-900 text-green-400 p-3 rounded text-sm overflow-x-auto">
                  {`# Backend (.env)
OPENAI_API_KEY=sk-your-key-here
SUPABASE_URL=https://your-project.supabase.co
SUPABASE_KEY=your-anon-key

# Redémarrer Docker après modification
docker compose down && docker compose up -d`}
                </pre>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Thème</h3>
                <div className="flex gap-4">
                  <button className="flex-1 p-4 border-2 border-blue-500 rounded-lg bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 bg-white border rounded"></div>
                      <span className="font-medium">Clair</span>
                    </div>
                    <p className="text-sm text-gray-500">Thème par défaut</p>
                  </button>
                  <button className="flex-1 p-4 border border-gray-200 rounded-lg bg-gray-50 opacity-50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 bg-gray-800 rounded"></div>
                      <span className="font-medium">Sombre</span>
                    </div>
                    <p className="text-sm text-gray-500">Bientôt disponible</p>
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">Langue</h3>
                <select className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="fr">🇫🇷 Français</option>
                  <option value="en" disabled>🇬🇧 English (bientôt)</option>
                </select>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Notifications de génération</h4>
                  <p className="text-sm text-gray-500">Alertes quand une leçon est générée</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Erreurs système</h4>
                  <p className="text-sm text-gray-500">Alertes en cas d'erreur</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* External links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a
          href="https://platform.openai.com/api-keys"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
        >
          <span className="text-2xl">🔑</span>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">OpenAI Dashboard</h4>
            <p className="text-sm text-gray-500">Gérer vos clés API</p>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-400" />
        </a>

        <a
          href="https://supabase.com/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
        >
          <Database className="w-6 h-6 text-emerald-600" />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">Supabase Dashboard</h4>
            <p className="text-sm text-gray-500">Gérer votre base de données</p>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-400" />
        </a>

        <a
          href="https://github.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
        >
          <span className="text-2xl">📚</span>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">Documentation</h4>
            <p className="text-sm text-gray-500">Guide d'utilisation</p>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-400" />
        </a>
      </div>
    </div>
  );
};

export default Settings;
